<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useToggle } from '@vueuse/core';
import { useAlert } from 'dashboard/composables';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import WhatsappBroadcastList from './WhatsappBroadcastList.vue';
import WhatsappBroadcastDialog from './WhatsappBroadcastDialog.vue';
import WhatsappBroadcastEmptyState from './WhatsappBroadcastEmptyState.vue';
import ConfirmDeleteDialog from './ConfirmDeleteDialog.vue';
import WhatsAppCampaignDeliveryReport from '../Campaigns/Pages/CampaignPage/WhatsAppCampaign/WhatsAppCampaignDeliveryReport.vue';
import WhatsappBroadcastRepliesModal from './WhatsappBroadcastRepliesModal.vue';

const { t } = useI18n();
const store = useStore();

const selectedCampaign = ref(null);
const [showDialog, toggleDialog] = useToggle();
const [isEditing, setIsEditing] = useToggle();
const [showReportDialog, toggleReportDialog] = useToggle();
const [showRepliesModal, toggleRepliesModal] = useToggle();
const confirmDeleteRef = ref(null);

const uiFlags = computed(() => store.getters['whatsappCampaigns/getUIFlags']);
const isFetching = computed(() => uiFlags.value.isFetching);
const campaigns = computed(
  () => store.getters['whatsappCampaigns/getWhatsappCampaigns']
);
const hasNoCampaigns = computed(
  () => campaigns.value?.length === 0 && !isFetching.value
);

const fetchCampaigns = async () => {
  await store.dispatch('whatsappCampaigns/get');
};

onMounted(() => {
  fetchCampaigns();
  store.dispatch('contactLists/get');
  store.dispatch('inboxes/get');
});

const handleCreate = () => {
  selectedCampaign.value = null;
  setIsEditing(false);
  toggleDialog(true);
};

const handleEdit = campaign => {
  selectedCampaign.value = campaign;
  setIsEditing(true);
  toggleDialog(true);
};

const handleDelete = campaign => {
  selectedCampaign.value = campaign;
  confirmDeleteRef.value?.open();
};

const handleToggle = async campaign => {
  try {
    await store.dispatch('whatsappCampaigns/toggle', campaign.id);
  } catch (error) {
    useAlert(t('WHATSAPP_BROADCAST.API.ERROR'));
  }
};

const handleCloseDialog = () => {
  toggleDialog(false);
  selectedCampaign.value = null;
  setIsEditing(false);
};

const handleRestart = async campaign => {
  try {
    await store.dispatch('whatsappCampaigns/restart', campaign.id);
    useAlert('Campanha reiniciada com sucesso');
  } catch (error) {
    useAlert(t('WHATSAPP_BROADCAST.API.ERROR'));
  }
};

const handleReport = campaign => {
  selectedCampaign.value = campaign;
  toggleReportDialog(true);
};

const handleCloseReportDialog = () => {
  toggleReportDialog(false);
  selectedCampaign.value = null;
};

const handleReplies = campaign => {
  selectedCampaign.value = campaign;
  toggleRepliesModal(true);
};

const handleCloseRepliesModal = () => {
  toggleRepliesModal(false);
  selectedCampaign.value = null;
};
</script>

<template>
  <div class="flex flex-col h-full bg-n-background">
    <header
      class="flex items-center justify-between px-6 py-4 border-b border-n-weak"
    >
      <h1 class="text-xl font-semibold text-n-slate-12">
        {{ t('WHATSAPP_BROADCAST.HEADER_TITLE') }}
      </h1>
      <Button icon="i-lucide-plus" color-scheme="primary" @click="handleCreate">
        {{ t('WHATSAPP_BROADCAST.NEW_CAMPAIGN') }}
      </Button>
    </header>

    <div class="flex-1 overflow-auto p-6">
      <div
        v-if="isFetching"
        class="flex items-center justify-center py-10 text-n-slate-11"
      >
        <Spinner />
      </div>

      <WhatsappBroadcastList
        v-else-if="!hasNoCampaigns"
        :campaigns="campaigns"
        @edit="handleEdit"
        @delete="handleDelete"
        @toggle="handleToggle"
        @report="handleReport"
        @restart="handleRestart"
        @replies="handleReplies"
      />

      <WhatsappBroadcastEmptyState v-else @create="handleCreate" />
    </div>

    <WhatsappBroadcastDialog
      v-if="showDialog"
      :campaign="selectedCampaign"
      :is-editing="isEditing"
      @close="handleCloseDialog"
    />

    <ConfirmDeleteDialog ref="confirmDeleteRef" :campaign="selectedCampaign" />

    <WhatsappBroadcastRepliesModal
      v-if="selectedCampaign"
      :campaign="selectedCampaign"
      :is-open="showRepliesModal"
      @close="handleCloseRepliesModal"
    />

    <WhatsAppCampaignDeliveryReport
      v-if="selectedCampaign"
      :campaign="selectedCampaign"
      :is-open="showReportDialog"
      campaign-type="whatsapp_campaign"
      @close="handleCloseReportDialog"
    />
  </div>
</template>
